/**
 * useBuyPlan — single-shot purchase of one plan via Culqi.
 *
 * Encapsulates the full Culqi-then-checkout dance so plan cards can simply
 * call `buy(plan)` and let the hook handle:
 *   1. Opening Culqi.js with the right amount.
 *   2. Sending the card token + plan id to `/api/billing/checkout`.
 *   3. Updating the auth store with the new balance / subscription.
 *   4. Surfacing busy state and error messages for the UI.
 *
 * No navigation, no cart. The cart on `/app/billing` is still available
 * for multi-item flows but most users will use this 1-click path.
 */

import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useCulqi } from '@/features/billing/useCulqi';
import type { CheckoutResponse, Plan } from '@/features/billing/types';
import { api } from '@/lib/api';
import { useAuthStore } from '@/stores/authStore';

export interface UseBuyPlanState {
  /** Plan id currently being charged, or null when idle. */
  busyPlanId: number | null;
  /** Last error message (cleared on next buy). */
  error: string | null;
  /** Culqi.js bootstrap state. */
  ready: boolean;
  /** Last successful purchase response — useful for "thank you" panes. */
  lastResult: CheckoutResponse | null;
  buy: (plan: Plan) => Promise<CheckoutResponse | null>;
  /** Aborta un checkout pendiente — útil para rescatar el estado "Procesando…"
   *  si Culqi no dispara el callback al cerrar el modal. */
  cancel: () => void;
  clearError: () => void;
}

export function useBuyPlan(): UseBuyPlanState {
  const user = useAuthStore((s) => s.user);
  const patchBilling = useAuthStore((s) => s.patchBilling);
  const qc = useQueryClient();
  const { ready, checkout, cancelCheckout } = useCulqi();

  const [busyPlanId, setBusyPlanId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lastResult, setLastResult] = useState<CheckoutResponse | null>(null);

  const buy = useCallback(
    async (plan: Plan): Promise<CheckoutResponse | null> => {
      if (!user) {
        setError('Necesitas iniciar sesión para comprar.');
        return null;
      }
      if (!ready) {
        setError('Procesador de pagos aún cargando. Reintenta en un segundo.');
        return null;
      }
      setBusyPlanId(plan.id);
      setError(null);
      try {
        const { token } = await checkout({
          amountCents: plan.price_cents,
          planName: plan.name,
          email: user.email,
        });
        const { data } = await api.post<CheckoutResponse>('/api/billing/checkout', {
          plan_id: plan.id,
          culqi_token: token,
        });
        // Reflect the new balance + sub state locally so the UI feels instant.
        patchBilling({
          tokens_balance: data.new_balance,
          subscription_expires_at: data.subscription_expires_at ?? undefined,
          subscription_status: data.subscription_expires_at
            ? 'active'
            : useAuthStore.getState().user?.subscription_status,
        });
        // Force a refetch of /me so the new role (subscriber) ends up in the
        // auth store next time the user navigates — keeps the role badge in
        // sync without a hard reload.
        try {
          const me = await api.get('/api/auth/me');
          useAuthStore.getState().setUser(me.data);
        } catch {
          /* non-fatal */
        }
        await Promise.all([
          qc.invalidateQueries({ queryKey: ['billing', 'me'] }),
          qc.invalidateQueries({ queryKey: ['billing', 'transactions'] }),
        ]);
        setLastResult(data);
        return data;
      } catch (err) {
        let msg = 'No se pudo completar el pago.';
        if (axios.isAxiosError(err)) {
          const detail = err.response?.data?.detail;
          if (typeof detail === 'string') msg = detail;
          else if (detail && typeof detail === 'object' && 'message' in detail) {
            msg = String((detail as { message: unknown }).message);
          }
        } else if (err instanceof Error) {
          msg = err.message;
        }
        setError(msg);
        return null;
      } finally {
        setBusyPlanId(null);
      }
    },
    [user, ready, checkout, patchBilling, qc],
  );

  return {
    busyPlanId,
    error,
    ready,
    lastResult,
    buy,
    cancel: () => {
      cancelCheckout();
      // El `finally` de buy() limpiará busyPlanId cuando la promesa
      // rechazada reaparezca. Pero si la promesa nunca resuelve por algún
      // bug, forzamos el reset acá también.
      setBusyPlanId(null);
    },
    clearError: () => setError(null),
  };
}
